import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { useShell } from '../../shell/ShellContext';
import { ShellRail, useShellUI } from '../../shell/AppShell';
import { leagueMeta } from '../../shell/leagueUtils';
import { rateMyTradeAPI } from '../../utils/api';
import TradeSides from './TradeSides';
import { SleeperAvatar, PlayerHeadshot } from '../../shell/SleeperAvatar';
import { formatPickDisplay, ktcOf } from './rmtContext';
import LinkSleeperBlock from '../../shell/LinkSleeperBlock';

/**
 * RateMyTradeCreate — the in-shell share wizard at /rate-my-trade/create.
 * Two paths: a REAL trade (pick from recent league transactions, mode
 * 'accepted') or a HYPOTHETICAL (pick an opponent, build both sides from
 * the live rosters, mode 'potential'). Same create endpoint as legacy.
 */

const POSITIONS = ['QB', 'RB', 'WR', 'TE', 'PICKS'];

const ItemRow = ({ selected, onClick, children }) => (
  <button
    onClick={onClick}
    style={{
      width: '100%', display: 'flex', alignItems: 'center', gap: 9, textAlign: 'left',
      padding: '7px 11px', borderRadius: 10, marginBottom: 4, fontSize: 13.5, fontWeight: 500,
      background: selected ? 'var(--ft-dark)' : 'var(--ft-surface)',
      color: selected ? '#fff' : 'var(--ft-ink)',
      transition: 'all .15s',
    }}
  >
    {children}
  </button>
);

const RateMyTradeCreate = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { sleeperUser, leagues, leaguesLoading } = useShell();
  const { toast } = useShellUI();

  const [mode, setMode] = useState(null); // 'accepted' | 'potential'
  const [league, setLeague] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [picked, setPicked] = useState(null);
  const [submitState, setSubmitState] = useState('idle');

  // Hypothetical builder state
  const [rosters, setRosters] = useState(null);
  const [opponent, setOpponent] = useState(null);
  const [tab, setTab] = useState('QB');
  const [myGives, setMyGives] = useState({ players: [], picks: [] });
  const [theirGives, setTheirGives] = useState({ players: [], picks: [] });
  const [reviewing, setReviewing] = useState(false);

  useEffect(() => {
    if (!league || mode !== 'accepted' || !sleeperUser?.userId) return undefined;
    let active = true;
    setTransactions(null);
    rateMyTradeAPI
      .getLeagueTransactions(league.leagueId, sleeperUser.userId)
      .then((res) => { if (active) setTransactions(Array.isArray(res.data) ? res.data : res.data?.transactions || []); })
      .catch(() => { if (active) setTransactions([]); });
    return () => { active = false; };
  }, [league, mode, sleeperUser]);

  useEffect(() => {
    if (!league || mode !== 'potential') return undefined;
    let active = true;
    setRosters(null);
    rateMyTradeAPI
      .getLeagueRosters(league.leagueId)
      .then((res) => { if (active) setRosters(res.data); })
      .catch(() => { if (active) { setRosters({ teams: [] }); toast('Could not load rosters'); } });
    return () => { active = false; };
  }, [league, mode, toast]);

  const myTeam = useMemo(
    () => rosters?.teams?.find((t) => t.sleeperUserId === sleeperUser?.userId) || null,
    [rosters, sleeperUser]
  );

  const resetLeague = () => {
    setLeague(null); setTransactions(null); setRosters(null); setOpponent(null);
    setMyGives({ players: [], picks: [] }); setTheirGives({ players: [], picks: [] });
    setReviewing(false); setPicked(null);
  };

  const toggle = (side, type, item) => {
    const setter = side === 'my' ? setMyGives : setTheirGives;
    setter((prev) => {
      const exists = prev[type].some((x) => x.id === item.id);
      return { ...prev, [type]: exists ? prev[type].filter((x) => x.id !== item.id) : [...prev[type], item] };
    });
  };
  const isSelected = (side, type, item) =>
    (side === 'my' ? myGives : theirGives)[type].some((x) => x.id === item.id);
  const totalSelected =
    myGives.players.length + myGives.picks.length + theirGives.players.length + theirGives.picks.length;
  const bothSides =
    myGives.players.length + myGives.picks.length > 0 && theirGives.players.length + theirGives.picks.length > 0;

  const post = async () => {
    setSubmitState('submitting');
    const payload = mode === 'accepted'
      ? {
          teamARosterId: picked.myRosterId,
          teamBRosterId: picked.otherRosterId,
          teamAGives: picked.teamAGives,
          teamBGives: picked.teamBGives,
        }
      : {
          teamARosterId: myTeam.rosterId,
          teamBRosterId: opponent.rosterId,
          teamAGives: myGives,
          teamBGives: theirGives,
        };
    try {
      const res = await rateMyTradeAPI.create({
        sleeperUsername: user.sleeperUsername,
        leagueId: league.leagueId,
        mode,
        ...payload,
      });
      const newShareId = res.data?.shareId || res.data?.trade?.shareId;
      toast('On the ballot — share the link anywhere');
      navigate(newShareId ? `/rate-my-trade/${newShareId}` : '/rate-my-trade');
    } catch (err) {
      setSubmitState('idle');
      toast(err?.response?.data?.error || 'Could not post — try again');
    }
  };

  const renderSidePanel = (team, side) => {
    // Picks whose rookie draft already ran are dead inventory; players sort
    // by market value so the list reads top-down like a trade target board.
    const draftedSeasons = new Set((rosters?.draftedSeasons || []).map(String));
    const items = tab === 'PICKS'
      ? (team.picks || []).filter((pk) => !draftedSeasons.has(String(pk.season)))
      : [...(team.players || [])]
          .filter((p) => p.position === tab)
          .sort((a, b) => ktcOf(b, rosters?.isSuperFlex) - ktcOf(a, rosters?.isSuperFlex) || (a.name || '').localeCompare(b.name || ''));
    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <SleeperAvatar id={team.avatar} name={team.ownerName} size={24} />
          <span style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {side === 'my' ? 'You send' : `${team.ownerName} sends`}
          </span>
        </div>
        {items.length === 0 ? (
          <p className="ft-sub" style={{ fontSize: 13 }}>Nothing here.</p>
        ) : tab === 'PICKS' ? (
          items.map((pk) => (
            <ItemRow key={pk.id} selected={isSelected(side, 'picks', pk)} onClick={() => toggle(side, 'picks', pk)}>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {formatPickDisplay(pk) || pk.displayName}
              </span>
            </ItemRow>
          ))
        ) : (
          items.map((p) => (
            <ItemRow key={p.id} selected={isSelected(side, 'players', p)} onClick={() => toggle(side, 'players', p)}>
              <PlayerHeadshot playerId={p.id} size={24} />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
              {p.team ? <span style={{ fontSize: 11, opacity: 0.55, flex: 'none' }}>{p.team}</span> : null}
            </ItemRow>
          ))
        )}
      </div>
    );
  };

  const hypotheticalTrade = { teamAGives: myGives, teamBGives: theirGives };

  return (
    <div className="ft-view" style={{ maxWidth: 860 }}>
      <Helmet><title>Share a trade — Rate My Trade — Fantasy Taco</title></Helmet>

      <ShellRail>
        <div className="ft-railsec">
          <span className="ft-k">What happens next</span>
          <div className="ft-progressrow">
            <span>{mode === 'potential' ? 'Build the trade' : 'Pick a real trade'}</span>
            <span className={`ft-chip${(picked || bothSides) ? ' green' : ''}`}>{(picked || bothSides) ? 'Done' : '—'}</span>
          </div>
          <div className="ft-progressrow"><span>It goes on the ballot</span><span className="ft-chip">—</span></div>
          <div className="ft-progressrow"><span>Share the link, votes roll in</span><span className="ft-chip">—</span></div>
        </div>
        <div className="ft-railsec">
          <span className="ft-k">Real vs hypothetical</span>
          <p className="ft-sub" style={{ fontSize: 13.5, marginTop: 8 }}>
            Real trades ask "did they win it?" Hypotheticals ask "should they do it?" — build one from any two rosters.
          </p>
        </div>
      </ShellRail>

      <div style={{ marginBottom: 18 }}>
        <button className="ft-btn sm ghost" onClick={() => navigate('/rate-my-trade')}>
          Back to the ballot
        </button>
      </div>

      <span className="ft-k brand">Rate My Trade</span>
      <h1 className="ft-hero">Put it to a vote.</h1>
      <p className="ft-sub">A real trade or a hypothetical. The internet takes it from there.</p>

      {!mode ? (
        <div className="ft-section">
          <span className="ft-k">What kind of trade</span>
          <div className="ft-rowcards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))' }}>
            <button className="ft-leaguecard" onClick={() => setMode('accepted')}>
              <div className="nm" style={{ marginBottom: 4 }}>A trade that happened</div>
              <div className="tm" style={{ marginBottom: 0, whiteSpace: 'normal' }}>
                Pulled straight from your league. Did you win it?
              </div>
            </button>
            <button className="ft-leaguecard" onClick={() => setMode('potential')}>
              <div className="nm" style={{ marginBottom: 4 }}>A hypothetical</div>
              <div className="tm" style={{ marginBottom: 0, whiteSpace: 'normal' }}>
                Still on the table. Build it and see what people think.
              </div>
            </button>
          </div>
        </div>
      ) : !league ? (
        <div className="ft-section">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <span className="ft-k">Which league</span>
            <button className="ft-fpill" onClick={() => setMode(null)}>Back</button>
          </div>
          {leaguesLoading ? (
            <div className="ft-loading">Loading your leagues…</div>
          ) : !sleeperUser?.username && leagues.length === 0 ? (
            <div style={{ marginTop: 14 }}>
              <LinkSleeperBlock note="Your account has no Sleeper username yet. Link it and your leagues appear here." />
            </div>
          ) : (
            <div className="ft-rowcards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
              {leagues.map((l) => (
                <button key={l.leagueId} className="ft-leaguecard" onClick={() => setLeague(l)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <SleeperAvatar id={l.avatar} name={l.name} size={36} radius="10px" />
                    <div style={{ minWidth: 0 }}>
                      <div className="nm" style={{ marginBottom: 1 }}>{l.name}</div>
                      <div className="tm" style={{ marginBottom: 0 }}>{leagueMeta(l)} · {l.season}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : mode === 'accepted' ? (
        !picked ? (
          <div className="ft-section">
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
              <span className="ft-k">Your recent trades — {league.name}</span>
              <button className="ft-fpill" onClick={resetLeague}>Change league</button>
            </div>
            {transactions === null ? (
              <div className="ft-loading">Pulling your trades from Sleeper…</div>
            ) : transactions.length === 0 ? (
              <p className="ft-sub" style={{ marginTop: 16 }}>
                No recent trades in this league. Pick another — or{' '}
                <button
                  style={{ textDecoration: 'underline', textUnderlineOffset: 3, background: 'none', padding: 0, fontSize: 'inherit', color: 'inherit' }}
                  onClick={() => setMode('potential')}
                >
                  build a hypothetical instead
                </button>.
              </p>
            ) : (
              <div style={{ marginTop: 14 }}>
                {transactions.map((tx) => (
                  <button
                    key={tx.transactionId}
                    className="ft-card"
                    style={{ display: 'block', width: '100%', maxWidth: 720, textAlign: 'left', marginBottom: 12, cursor: 'pointer' }}
                    onClick={() => setPicked(tx)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <span style={{ fontSize: 14, fontWeight: 600 }}>vs {tx.otherOwnerName || 'league mate'}</span>
                      <span className="ft-chip outline">{new Date(tx.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    </div>
                    <TradeSides trade={tx} labelA="You got" labelB="You sent" />
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="ft-section">
            <span className="ft-k">On the ballot</span>
            <div className="ft-card white" style={{ padding: 24, maxWidth: 720, marginTop: 12 }}>
              <TradeSides trade={picked} labelA="You got" labelB="You sent" />
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button className="ft-btn" disabled={submitState !== 'idle'} onClick={post}>
                {submitState === 'submitting' ? 'Posting…' : 'Post it'}
              </button>
              <button className="ft-btn ghost" onClick={() => setPicked(null)}>Back</button>
            </div>
          </div>
        )
      ) : /* mode === 'potential' */ rosters === null ? (
        <div className="ft-loading">Pulling the rosters…</div>
      ) : !myTeam ? (
        <p className="ft-sub" style={{ marginTop: 20 }}>Couldn't find your roster in {league.name}. <button className="ft-fpill" onClick={resetLeague}>Pick another league</button></p>
      ) : !opponent ? (
        <div className="ft-section">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <span className="ft-k">Trading with — {league.name}</span>
            <button className="ft-fpill" onClick={resetLeague}>Change league</button>
          </div>
          <div className="ft-rowcards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))' }}>
            {(rosters.teams || [])
              .filter((t) => t.rosterId !== myTeam.rosterId && t.ownerName)
              .map((t) => (
                <button key={t.rosterId} className="ft-leaguecard" onClick={() => setOpponent(t)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <SleeperAvatar id={t.avatar} name={t.ownerName} size={32} />
                    <div style={{ minWidth: 0 }}>
                      <div className="nm" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.ownerName}</div>
                      <div className="tm" style={{ marginBottom: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.teamName}</div>
                    </div>
                  </div>
                </button>
              ))}
          </div>
        </div>
      ) : !reviewing ? (
        <div className="ft-section">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <span className="ft-k">Build it — you and {opponent.ownerName}</span>
            <button className="ft-fpill" onClick={() => { setOpponent(null); setMyGives({ players: [], picks: [] }); setTheirGives({ players: [], picks: [] }); }}>
              Change opponent
            </button>
          </div>

          <div className="ft-pillrow" style={{ margin: '14px 0 16px' }}>
            {POSITIONS.map((p) => (
              <button key={p} className={`ft-fpill${tab === p ? ' on' : ''}`} onClick={() => setTab(p)}>
                {p === 'PICKS' ? 'Picks' : p}
              </button>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, maxWidth: 760 }}>
            {renderSidePanel(myTeam, 'my')}
            {renderSidePanel(opponent, 'their')}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 22 }}>
            <button className="ft-btn" disabled={!bothSides} onClick={() => setReviewing(true)}>
              Review the trade{totalSelected ? ` · ${totalSelected}` : ''}
            </button>
            {!bothSides && <span className="ft-sub" style={{ fontSize: 13 }}>Pick at least one piece from each side.</span>}
          </div>
        </div>
      ) : (
        <div className="ft-section">
          <span className="ft-k">On the ballot — hypothetical</span>
          <div className="ft-card white" style={{ padding: 24, maxWidth: 720, marginTop: 12 }}>
            <TradeSides trade={hypotheticalTrade} teamA={myTeam} teamB={opponent} labelA="You send" labelB={`${opponent.ownerName} sends`} />
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
            <button className="ft-btn" disabled={submitState !== 'idle'} onClick={post}>
              {submitState === 'submitting' ? 'Posting…' : 'Post it'}
            </button>
            <button className="ft-btn ghost" onClick={() => setReviewing(false)}>Back to building</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RateMyTradeCreate;
